import React, { RefObject, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Namespace } from '../../../../translation/namespace';
import { Image, ImageModifier } from '../../../../components/molecules/Image';
import { SkipableContent } from '../SkipableContent';
import { HiddenGalleryHeadline } from '../HiddenGalleryHeadline';
import ModalDialog from '../../../../components/organisms/ModalDialog/ModalDialog';
import { ModalDialogModifier } from '../../../../components/organisms/ModalDialog/ModalDialog.modifier';
import { PopupSliderGallery } from './PopupSliderGallery';
import { CarouselGallery } from '../Carousel/CarouselGallery';
import { CarouselListItem } from '../Carousel/CarouselListItem';
import { GalleryImage } from './ThumbnailGallery';
import { ImageState } from '../../../../types/api/ImageType';

type GalleryProps = {
  id: string;
  images: GalleryImage[];
};

export function SliderGallery({ id, images }: GalleryProps) {
  const { t } = useTranslation(Namespace.ImageGallery);
  const displayableImages = images.filter(({ image }) => image.imageState !== ImageState.pending);

  const [activeImage, setActiveImage] = useState<null | string>(null);

  /**
   * Necessary for empty image galleries
   * No images => no rendering.
   */
  if (displayableImages.length === 0) {
    return null;
  }

  return (
    <div className="image-gallery">
      <HiddenGalleryHeadline id={id}>{t('headlineSliderGallery')}</HiddenGalleryHeadline>
      <SkipableContent id={id}>
        <CarouselGallery>
          {images.map(({ image }, index) => {
            const popUpImage = { ...image, popUp: true };
            return (
              <CarouselListItem
                key={`${id}-${image.imageId}`}
                id={id}
                total={images.length}
                currentIndex={index}
                tooltipPlacement="outside"
              >
                <Image
                  image={popUpImage}
                  lazyLoading
                  responsiveType="imageGalleryImage"
                  modifier={ImageModifier.SLIDER}
                  onClickForPopUp={() => setActiveImage(image.imageId)}
                />
              </CarouselListItem>
            );
          })}
        </CarouselGallery>
      </SkipableContent>
      {activeImage && (
        <ModalDialog
          headline={t('headlineModalWithSliderGallery')}
          closeModal={() => {
            setActiveImage(null);
          }}
          modifier={[ModalDialogModifier.POP_UP, ModalDialogModifier.SLIDER_IN_POP_UP]}
        >
          {focusRef => (
            <PopupSliderGallery
              id={`popup-${id}`}
              images={images}
              ref={focusRef as RefObject<HTMLLIElement>}
              focusedImageId={activeImage}
            />
          )}
        </ModalDialog>
      )}
    </div>
  );
}
